/**
 * @module components/ACS/ACSAlertsBanner
 * @description Banner de alertas ACS activas sobre propuestas vencidas.
 *
 * Se renderiza en la parte superior del layout cuando hay propuestas ACS
 * que llevan más de N días sin decisión del CISO. Las alertas son event-driven
 * (calculadas en el cliente al cargar el contexto) sin necesidad de un cron job.
 *
 * El banner es dismissible por sesión pero reaparece en la próxima carga
 * si las propuestas siguen pendientes.
 *
 * @see ACSContext que calcula las alertas en `loadAlerts()`
 * @see App.tsx que renderiza este banner dentro de ACSProvider
 */
// ============================================================
// ACSAlertsBanner — Banner de alertas ACS activas
// Alertas event-driven (sin cron) sobre propuestas vencidas
// ============================================================

import React from 'react';
import { AlertTriangle, AlertCircle, X } from 'lucide-react';
import { useACS } from '../../contexts/ACSContext';

export function ACSAlertsBanner() {
  const { alerts, dismissAlert } = useACS();

  const activeAlerts = alerts.filter(a => a.status === 'active').slice(0, 3);

  if (activeAlerts.length === 0) return null;

  return (
    <div className="space-y-1">
      {activeAlerts.map(alert => {
        const Icon = alert.severity === 'critical' ? AlertCircle : AlertTriangle;
        const cls = alert.severity === 'critical'
          ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'
          : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300';

        return (
          <div key={alert.id} className={`flex items-center gap-3 px-4 py-2 border text-xs ${cls}`}>
            <Icon className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="flex-1">[ACS] {alert.contextLabel}: {alert.message}</span>
            <button
              onClick={() => dismissAlert(alert.id)}
              className="p-0.5 hover:opacity-70 flex-shrink-0"
              title="Descartar alerta"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
